import { useCallback, useState, useEffect } from 'react';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import Stack from '@mui/material/Stack';
import Container from '@mui/material/Container';
import { moveColumn, moveTask } from 'src/api/kanban';
import EmptyContent from 'src/components/empty-content';
import KanbanColumn from '../kanban-column';
import KanbanColumnAdd from '../kanban-column-add';
import { KanbanColumnSkeleton } from '../kanban-skeleton';
// import image1 from '../../../../../../../assets/icons/document.png';
// import image from '../../../../../../../assets/images/dummy.png';
import FilterPage from '../kanban-filters';
import { getRequest, getRequestMessage } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { useSnackbar } from 'notistack';

const defaultFilters = {
  name: '',
  role: [],
  status: 'all',
  active: 'all',
};

export default function KanbanView({ filters, onFilters }) {
  const [ board, setBoard ] = useState(null);
  // const [ filters, setFilters ] = useState(defaultFilters);
  const [ data, setData ] = useState([]);
  console.log("🚀 ~ KanbanView ~ data:", data)
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    getKanbanData();
  }, []);

  const getKanbanData = async () => {
    const response = await getRequest(`${endpoints.admin_dashboard.marketing_management.board.crud_stage}`);
    if (response) {
      setData(response);
      const transformed = BoardData(response);
      setBoard(transformed);
    }
  };

  const BoardData = (payload) => {
    const columns = {};
    const ordered = [];
    const tasks = {};

    payload?.forEach((column) => {
      const columnId = `column-${column.id}`;
      const taskIds = [];
      column?.cards?.forEach((card) => {
        const taskId = `task-${card.id}`;
        let labels = card?.labels;

        // Check if labels is a string and not already a JSON array string
        if (typeof labels === 'string') {
          // If it's a comma-separated string, convert it into a JSON array string
          if (!labels.startsWith('[')) {
            labels = `["${labels.split(',').join('","')}"]`;
          }
        }

        try {
          // Safely parse labels if it's a valid JSON string
          labels = JSON.parse(labels || '[]');
        } catch (error) {
          // If parsing fails, log an error and use an empty array as fallback
          console.error('Failed to parse labels:', labels);
          labels = [];
        }

        tasks[ taskId ] = {
          id: taskId,
          name: card.title,
          description: card.description,
          status: card.status,
          priority: card.priority,
          due: [ card.due_date ],
          labels: labels,
          assignee: card.assigned_users.map((user) => ({
            id: user.id,
            name: `${user.first_name} ${user.last_name}`,
            avatarUrl: user.avatar_url || '',
          })),
          reporter: {
            id: column.created_by.id,
            name: `${column.created_by.first_name} ${column.created_by.last_name}`,
            avatarUrl: '',
          },
          comments: [],
          attachments: [],
        };

        taskIds.push(taskId);
      });

      columns[ columnId ] = {
        id: columnId,
        name: column.title,
        taskIds,
      };
      ordered.push(columnId);
    });

    return { columns, tasks, ordered };
  };


  const onDragEnd = useCallback(
    async ({ destination, source, draggableId, type }) => {
      if (!destination || !board) return;
      if (
        destination.droppableId === source.droppableId &&
        destination.index === source.index
      )
        return;
      if (type === 'COLUMN') {
        const newOrdered = [ ...board.ordered ];
        newOrdered.splice(source.index, 1);
        newOrdered.splice(destination.index, 0, draggableId);
        const updatedBoard = { ...board, ordered: newOrdered };
        setBoard(updatedBoard);
        moveColumn(newOrdered);
        return;
      }
      const sourceCol = board.columns[ source.droppableId ];
      const destCol = board.columns[ destination.droppableId ];
      const sourceTaskIds = [ ...sourceCol.taskIds ];
      const destTaskIds = [ ...destCol.taskIds ];

      const cardId = draggableId.split('-')[ 1 ];
      const targetColumnId = destCol.id.split('-')[ 1 ];

      try {
        const response = await getRequestMessage(
          `${endpoints.admin_dashboard.marketing_management.board.drag_card}/${cardId}/move-to-kanban-stage/${targetColumnId}`,
        );
        console.log("🚀?????????????????????????????? ~ response", response)

        if (response) {
          sourceTaskIds.splice(source.index, 1);
          destTaskIds.splice(destination.index, 0, draggableId);
          const updatedColumns = {
            ...board.columns,
            [ sourceCol.id ]: { ...sourceCol, taskIds: sourceTaskIds },
            [ destCol.id ]: { ...destCol, taskIds: destTaskIds },
          };
          setBoard({ ...board, columns: updatedColumns });
          moveTask({ board: { columns: updatedColumns } });
          enqueueSnackbar(response?.metadata?.message, {
            variant: 'success',
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
          });
        } else {
          enqueueSnackbar(response?.metadata?.message, {
            variant: 'error',
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
          });
        }
      } catch (error) {
        console.error('Failed to move card to new stage:', error);

      }
    },
    [ board, enqueueSnackbar ]
  );
  // const handleFilters = useCallback(
  //   (name, value) => {
  //     setFilters((prevState) => ({
  //       ...prevState,
  //       [ name ]: value,
  //     }));
  //   },
  //   []
  // );

  const renderSkeleton = (
    <Stack direction="row" alignItems="flex-start" spacing={ 3 }>
      { [ ...Array(3) ].map((_, index) => (
        <KanbanColumnSkeleton key={ index } index={ index } />
      )) }
    </Stack>
  );
  if (!board) return renderSkeleton;
  const boardEmpty = board.ordered.length === 0;
  return (
    <Container maxWidth={ false } sx={ { height: 1 } }>

      { boardEmpty && (
        <EmptyContent
          filled
          title="No Data"
          sx={ { py: 10, maxHeight: { md: 480 } } }
        />
      ) }

      { !boardEmpty && (
        <DragDropContext onDragEnd={ onDragEnd }>
          <Droppable droppableId="board" type="COLUMN" direction="horizontal">
            { (provided) => (
              <Stack
                sx={ {
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  height: '100%',
                  pb: 2,
                } }
              >
                <Stack
                  ref={ provided.innerRef }
                  { ...provided.droppableProps }
                  spacing={ 3 }
                  direction="row"
                  alignItems="flex-start"
                  sx={ { minWidth: 'max-content', p: 0.5 } }
                >
                  { board.ordered
                    .map((columnId) => board.columns[ columnId ])
                    .filter((col) =>
                      col.taskIds.some((taskId) =>
                        board.tasks[ taskId ]?.name?.toLowerCase()?.includes(filters.name)
                      )
                    )
                    .map((column, index) => (
                      <KanbanColumn
                        key={ column.id }
                        index={ index }
                        column={ column }
                        tasks={ board.tasks }
                        reload={ getKanbanData }
                      />
                    )) }

                  { provided.placeholder }
                  <KanbanColumnAdd
                    reload={ () => getKanbanData() }
                    newcol={ board?.ordered?.length }
                  />
                </Stack>
              </Stack>
            ) }
          </Droppable>
        </DragDropContext>
      ) }
    </Container>
  );
} 